import React, { useContext, useEffect, useRef, useState } from 'react';
import VendorForm from '../../views/vendor/VendorForm';
import VendorContact from '../../views/vendor/VendorContact';
import VendorWebsites from '../../views/vendor/tables/VendorWebsites';
import VendorFiles from '../../views/vendor/VendorFiles';
import VendorAccounts from '../../views/vendor/VendorAccounts';
import LinkedAccounts from '../../views/vendor/LinkedAccounts';
import DualVendors from '../../views/vendor/DualVendors';
import { VendorContext } from '../../context/VendorProvider';
import { UserContext } from '../../context/UserProvider';
import { AiOutlineRight, AiOutlineLeft } from 'react-icons/ai';
import { FaInfoCircle, FaGlobe, FaAddressBook, FaFile, FaLink } from 'react-icons/fa';
import { MdPeopleAlt } from 'react-icons/md';
import { BsDatabaseFillDown } from 'react-icons/bs';

const VendorModal = ({ onClose }) => {
  const {
    nextVendor,
    previousVendor,
    vendors,
    selectedVendor,
    setSelectedVendor,
  } = useContext(VendorContext);

  const { setIsExpanded: setGlobalIsExpanded } = useContext(UserContext);

  const [displayTitle, setDisplayTitle] = useState('');
  const [selectedLink, setSelectedLink] = useState('');
  const modalRef = useRef();
  const formRef = useRef(null);
  const contactRef = useRef(null);
  const accountRef = useRef(null);
  const websitesRef = useRef(null);
  const filesRef = useRef(null);
  const linkedRef = useRef(null);
  const dualRef = useRef(null);
  const sidebarRef = useRef(null);

  const currentIndex = selectedVendor
    ? vendors.findIndex((v) => v.rowKey === selectedVendor.rowKey)
    : -1;
  const vendorName = selectedVendor ? selectedVendor.licensee : 'New Vendor';
  const vendorId = selectedVendor ? selectedVendor.rowKey : null;

  useEffect(() => {
    if (vendorName) {
      setDisplayTitle(vendorName);
    } else {
      setDisplayTitle('New Vendor');
    }
  }, [vendorName]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setSelectedLink(entry.target.id);
          }
        });
      },
      { threshold: [0.3, 0.7] }
    );

    const sections = [
      formRef,
      contactRef,
      websitesRef,
      accountRef,
      filesRef,
      linkedRef,
      dualRef,
    ]
      .filter((ref) => ref.current)
      .map((ref) => ref.current);

    sections.forEach((element) => observer.observe(element));

    return () => {
      sections.forEach((element) => {
        if (element) observer.unobserve(element);
      });
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        e.target.tagName.toLowerCase() !== 'input' &&
        e.target.tagName.toLowerCase() !== 'textarea' &&
        e.key === 'Escape'
      ) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentIndex, vendors.length, onClose]);

  useEffect(() => {
    const containers = modalRef.current.querySelectorAll(
      '.scroll-vendor-snap-container'
    );
    if (!containers.length) return;

    const isChrome =
      /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
    if (isChrome) {
      containers.forEach((container) => {
        container.classList.add('chrome-browser');
      });
    }

    const debounce = (func, wait) => {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
      };
    };

    containers.forEach((container) => {
      let lastScrollTop = 0;

      const handleScrollDirection = debounce(() => {
        const currentScrollTop = container.scrollTop;
        if (currentScrollTop < lastScrollTop) {
          container.classList.add('scroll-up');
        } else {
          container.classList.remove('scroll-up');
        }
        lastScrollTop = currentScrollTop <= 0 ? 0 : currentScrollTop;
      }, 50);

      container.addEventListener('scroll', handleScrollDirection, {
        passive: true,
      });

      return () => {
        container.removeEventListener('scroll', handleScrollDirection);
      };
    });
  }, []);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const scrollToVendorComponent = (ref) => {
    if (ref && ref.current) {
      const container = modalRef.current.querySelector(
        '.scroll-vendor-snap-container'
      );
      if (!container) return;

      requestAnimationFrame(() => {
        const offset = 10;
        const elementPosition = ref.current.offsetTop - container.offsetTop - offset;

        container.scrollTo({
          top: elementPosition,
          behavior: 'smooth',
        });

        setTimeout(() => {
          ref.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }, 300);
      });
    }
  };

  const renderNavLink = (icon, text, ref, linkId, tooltip) => {
    return (
      <button
        onClick={() => scrollToVendorComponent(ref)}
        className={`flex items-center text-left text-blue-900 hover:text-blue-700 dark:text-indigo-200 min-w-full p-1.5 font-semibold transition-colors duration-300 ${
          selectedLink === linkId ? 'rounded bg-blue-100 dark:bg-stone-600' : ''
        }`}
        title={tooltip}
      >
        {icon}
        <span className="ml-2">{text}</span>
      </button>
    );
  };

  const hasRowKey = selectedVendor && selectedVendor.rowKey;

  const handleVendorDataChange = (updatedVendor) => {
    setSelectedVendor(updatedVendor);
  };

  const modalStyle =
    'fixed inset-0 bg-white dark:bg-stone-900 z-50 pt-20 transition-all duration-500 ease-in-out max-h-screen flex flex-col';

  return (
    <div
      id="vendor-modal"
      className="fixed inset-0 z-50 flex items-start justify-center bg-stone-950 bg-opacity-80 pt-10 dark:bg-opacity-95"
    >
      <div ref={modalRef} className={modalStyle}>
        <div className="flex items-center justify-between border-b border-stone-200 px-4 py-2 dark:border-stone-600">
          <div className="min-w-0 flex-1" title={vendorName}>
            <h2 className="text-lg font-bold dark:text-gray-200">
              {displayTitle}
            </h2>
          </div>
          <div className="absolute left-0 right-0 flex select-none items-center justify-center">
            {currentIndex > 0 && currentIndex !== -1 && (
              <button onClick={previousVendor} className="flex-shrink-0">
                <AiOutlineLeft
                  className="mb-1 inline-block h-4 w-4 cursor-pointer font-bold dark:text-gray-400"
                  title="previous vendor"
                />
              </button>
            )}
            <span className="mx-2 text-gray-600 dark:text-gray-400">
              {currentIndex !== -1
                ? `${currentIndex + 1} / ${vendors.length}`
                : ''}
            </span>
            {currentIndex < vendors.length - 1 && currentIndex !== -1 && (
              <button onClick={nextVendor} className="flex-shrink-0">
                <AiOutlineRight
                  className="mb-1 inline-block h-4 w-4 cursor-pointer font-bold dark:text-gray-400"
                  title="next vendor"
                />
              </button>
            )}
          </div>
          <button
            onClick={() => {
              setGlobalIsExpanded(false);
              onClose();
            }}
            className="z-50"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-gray-600 hover:text-gray-800 dark:text-gray-500 dark:hover:text-gray-700"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div className="flex flex-1 overflow-hidden">
          <div
            ref={sidebarRef}
            className="w-40 sidebar-navigation flex flex-col h-full overflow-y-auto border-r bg-white px-6 py-6 dark:border-stone-600 dark:bg-stone-800"
          >
            <ul className="list-none flex-1">
              <li className="mb-4" title="General information">
                {renderNavLink(
                  <FaInfoCircle />,
                  'General',
                  formRef,
                  'vendor-general',
                  'General Information'
                )}
              </li>
              {hasRowKey && (
                <>
                  <li className="mb-4" title="Websites">
                    {renderNavLink(
                      <FaGlobe />,
                      'Websites',
                      websitesRef,
                      'vendor-websites',
                      'Websites'
                    )}
                  </li>
                  <li className="mb-4" title="Contacts">
                    {renderNavLink(
                      <FaAddressBook />,
                      'Contacts',
                      contactRef,
                      'vendor-contacts',
                      'Contacts'
                    )}
                  </li>
                  <li className="mb-4" title="Accounts">
                    {renderNavLink(
                      <MdPeopleAlt />,
                      'Accounts',
                      accountRef,
                      'vendor-accounts',
                      'Accounts'
                    )}
                  </li>
                  <li className="mb-4" title="Files">
                    {renderNavLink(
                      <FaFile />,
                      'Files',
                      filesRef,
                      'vendor-files',
                      'Files'
                    )}
                  </li>
                  <li className="mb-4" title="Associated">
                    {renderNavLink(
                      <FaLink />,
                      'Linked',
                      linkedRef,
                      'vendor-linked',
                      'Linked Accounts'
                    )}
                  </li>
                  {selectedVendor && selectedVendor.isProvider && (
                    <li className="mb-4" title="Dual Vendors">
                      {renderNavLink(
                        <BsDatabaseFillDown />,
                        'Consumers',
                        dualRef,
                        'vendor-dual',
                        'Dual Vendors'
                      )}
                    </li>
                  )}
                </>
              )}
            </ul>
            <div className="mt-auto">
              <button
                onClick={onClose}
                title="Close Window"
                className="mb-6 flex w-full items-center justify-center p-1.5 text-xs font-bold text-blue-900 transition-colors duration-300 hover:text-blue-700 dark:text-indigo-400"
              >
                Close Window
              </button>
            </div>
          </div>
          <div className="scroll-vendor-snap-container flex-1 overflow-y-auto py-3">
            <div
              ref={formRef}
              id="vendor-general"
              className="scroll-vendor-snap-section min-w-[800px] flex-shrink-0"
            >
              <VendorForm
                vendor={selectedVendor}
                onClose={onClose}
                onVendorDataChange={handleVendorDataChange}
              />
            </div>
            {hasRowKey && (
              <>
                <div
                  ref={websitesRef}
                  id="vendor-websites"
                  className="scroll-vendor-snap-section min-w-[800px] flex-shrink-0 pt-3"
                >
                  <VendorWebsites vendorId={vendorId} vendorName={vendorName} />
                </div>
                <div
                  ref={contactRef}
                  id="vendor-contacts"
                  className="scroll-vendor-snap-section min-w-[800px] flex-shrink-0"
                >
                  <VendorContact vendorId={vendorId} vendorName={vendorName} />
                </div>
                <div
                  ref={accountRef}
                  id="vendor-accounts"
                  className="scroll-vendor-snap-section min-w-[800px] flex-shrink-0"
                >
                  <VendorAccounts
                    key={vendorId}
                    vendorId={vendorId}
                    vendorName={vendorName}
                  />
                </div>
                <div
                  ref={filesRef}
                  id="vendor-files"
                  className="scroll-vendor-snap-section min-w-[800px] flex-shrink-0"
                >
                  <VendorFiles vendorId={vendorId} />
                </div>
                <div
                  ref={linkedRef}
                  id="vendor-linked"
                  className="scroll-vendor-snap-section min-w-[800px] flex-shrink-0"
                >
                  <LinkedAccounts vendorId={vendorId} />
                </div>
                {selectedVendor && selectedVendor.isProvider && (
                  <div
                    ref={dualRef}
                    id="vendor-dual"
                    className="scroll-vendor-snap-section min-w-[800px] flex-shrink-0"
                  >
                    <DualVendors vendorId={vendorId} />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorModal;